import { createEntityRegistry, type BodyHandle, type ShapeHandle, type ShapeIdentity } from 'box3d-web';
export type { ShapeIdentity } from 'box3d-web';
export { MAX_NATIVE_SHAPE_INDEX, MAX_NATIVE_WORLDS, MAX_NATIVE_GENERATION } from 'box3d-web';

import type {
  CellIdentity,
  DestructionIdentity,
  ElementIdentity,
  FragmentIdentity,
  LayerIdentity,
  StructureIdentity,
  WorldIdentity,
} from './identities';

/** Actors owned by the shared physics world rather than by a structure. */
export type WorldActor = 'ground' | 'projectile';

/** The semantic address of a world-owned body. */
export interface WorldActorAddress {
  readonly scope: 'world';
  readonly worldId: WorldIdentity;
  readonly actor: WorldActor;
  /** A stable caller-owned ordinal (for example, a projectile sequence). */
  readonly ordinal: number;
  readonly materialId?: string;
}

/** The semantic address of a body belonging to a destructible structure. */
export interface StructureEntityAddress {
  readonly scope: 'structure';
  readonly worldId: WorldIdentity;
  readonly structureId: StructureIdentity;
  readonly elementId?: ElementIdentity;
  readonly layerId?: LayerIdentity;
  readonly cellId?: CellIdentity;
  readonly fragmentId?: FragmentIdentity;
  /** Existing material/profile name; the registry does not interpret it. */
  readonly materialId?: string;
}

export type PhysicsEntityAddress = WorldActorAddress | StructureEntityAddress;

export interface RegisteredBody {
  readonly body: BodyHandle;
  readonly address: PhysicsEntityAddress;
}

export interface RegisteredShape {
  readonly shape: ShapeHandle;
  readonly identity: ShapeIdentity;
  readonly body: BodyHandle;
  readonly bodyRegistration: RegisteredBody;
}

export interface PhysicsEntityRegistryStats {
  readonly bodyCount: number;
  readonly shapeCount: number;
  readonly structureCount: number;
  readonly structureBodyCount: number;
  readonly worldActorCount: number;
}

export interface PhysicsEntityRegistrySnapshot {
  readonly stats: PhysicsEntityRegistryStats;
  readonly bodies: readonly RegisteredBody[];
  readonly shapes: readonly RegisteredShape[];
}

type IdentityKind = DestructionIdentity['kind'];

const IDENTITY_KINDS: readonly IdentityKind[] = [
  'world',
  'structure',
  'element',
  'layer',
  'cell',
  'fragment',
];

function assertObject(value: unknown, label: string): asserts value is object {
  if (value === null || typeof value !== 'object') {
    throw new TypeError(`${label} must be a destruction identity`);
  }
}

function assertIdentity(
  value: unknown,
  expectedKind: IdentityKind,
  label: string,
): asserts value is DestructionIdentity {
  assertObject(value, label);
  const identity = value as Partial<DestructionIdentity>;
  if (identity.kind !== expectedKind) {
    throw new TypeError(`${label} must be a ${expectedKind} identity`);
  }
  if (!IDENTITY_KINDS.includes(identity.kind)) {
    throw new TypeError(`${label} has an unknown identity kind`);
  }
  if (typeof identity.namespace !== 'string' || identity.namespace.trim().length === 0) {
    throw new TypeError(`${label} has an invalid namespace`);
  }
  const generation = identity.generation;
  if (typeof generation !== 'number' || !Number.isSafeInteger(generation) || generation < 0) {
    throw new RangeError(`${label} has an invalid generation`);
  }
  const ordinal = identity.ordinal;
  if (typeof ordinal !== 'number' || !Number.isSafeInteger(ordinal) || ordinal < 0) {
    throw new RangeError(`${label} has an invalid ordinal`);
  }
  if (typeof identity.key !== 'string' || identity.key.length === 0) {
    throw new TypeError(`${label} has an invalid key`);
  }
}

function assertAttached(
  child: DestructionIdentity,
  parent: DestructionIdentity,
  label: string,
): void {
  if (
    child.parent?.key !== parent.key
    || child.namespace !== parent.namespace
    || child.generation !== parent.generation
  ) {
    throw new Error(`${label} identity is not attached to the supplied parent`);
  }
}

function assertWorldIdentity(value: unknown, label: string): asserts value is WorldIdentity {
  assertIdentity(value, 'world', label);
  if ('parent' in value && value.parent !== undefined) {
    throw new Error(`${label} world identity cannot have a parent`);
  }
}

function checkedMaterialId(value: string | undefined, label: string): string | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${label} must be a non-empty string when supplied`);
  }
  return value;
}

function checkedOrdinal(value: number, label: string): number {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(`${label} must be a non-negative safe integer`);
  }
  return value;
}

function assertStructureHierarchy(address: StructureEntityAddress): void {
  assertWorldIdentity(address.worldId, 'worldId');
  assertIdentity(address.structureId, 'structure', 'structureId');
  assertAttached(address.structureId, address.worldId, 'structureId');

  if (address.elementId !== undefined) {
    assertIdentity(address.elementId, 'element', 'elementId');
    assertAttached(address.elementId, address.structureId, 'elementId');
  }
  if (address.layerId !== undefined) {
    assertIdentity(address.layerId, 'layer', 'layerId');
    if (address.elementId === undefined) throw new Error('layerId requires elementId');
    assertAttached(address.layerId, address.elementId, 'layerId');
  }
  if (address.cellId !== undefined) {
    assertIdentity(address.cellId, 'cell', 'cellId');
    if (address.layerId === undefined) throw new Error('cellId requires layerId');
    assertAttached(address.cellId, address.layerId, 'cellId');
  }
  if (address.fragmentId !== undefined) {
    assertIdentity(address.fragmentId, 'fragment', 'fragmentId');
    const parent = address.fragmentId.parent;
    if (parent?.kind === 'cell') {
      if (address.cellId === undefined) throw new Error('cell-parent fragmentId requires cellId');
      assertAttached(address.fragmentId, address.cellId, 'fragmentId');
    } else if (parent?.kind === 'layer') {
      if (address.layerId === undefined) throw new Error('layer-parent fragmentId requires layerId');
      if (address.cellId !== undefined) throw new Error('layer-parent fragmentId cannot have cellId');
      assertAttached(address.fragmentId, address.layerId, 'fragmentId');
    } else {
      throw new Error('fragmentId requires a cell or layer parent');
    }
  }
  checkedMaterialId(address.materialId, 'materialId');
}

function checkedAddress(address: PhysicsEntityAddress): PhysicsEntityAddress {
  assertObject(address, 'address');
  if (address.scope === 'world') {
    assertWorldIdentity(address.worldId, 'worldId');
    if (address.actor !== 'ground' && address.actor !== 'projectile') {
      throw new TypeError('world address actor must be ground or projectile');
    }
    checkedOrdinal(address.ordinal, 'address.ordinal');
    checkedMaterialId(address.materialId, 'materialId');
    return Object.freeze({
      scope: 'world',
      worldId: address.worldId,
      actor: address.actor,
      ordinal: address.ordinal,
      ...(address.materialId === undefined ? {} : { materialId: address.materialId }),
    });
  }
  if (address.scope === 'structure') {
    assertStructureHierarchy(address);
    return Object.freeze({
      scope: 'structure',
      worldId: address.worldId,
      structureId: address.structureId,
      ...(address.elementId === undefined ? {} : { elementId: address.elementId }),
      ...(address.layerId === undefined ? {} : { layerId: address.layerId }),
      ...(address.cellId === undefined ? {} : { cellId: address.cellId }),
      ...(address.fragmentId === undefined ? {} : { fragmentId: address.fragmentId }),
      ...(address.materialId === undefined ? {} : { materialId: address.materialId }),
    });
  }
  throw new TypeError('address scope must be world or structure');
}

function addressKey(address: PhysicsEntityAddress): string {
  if (address.scope === 'world') {
    return `world:${address.worldId.key}:${address.actor}:${address.ordinal}`;
  }
  const leaf = address.fragmentId ?? address.cellId ?? address.layerId ?? address.elementId ?? address.structureId;
  return `structure:${leaf.key}`;
}

function groupKey(address: PhysicsEntityAddress): string | undefined {
  return address.scope === 'structure' ? address.structureId.key : undefined;
}

function addressWorldKey(address: PhysicsEntityAddress): string {
  return address.worldId.key;
}

/**
 * Instance-local semantic routing for Box3D handles.
 *
 * The registry does not allocate or recycle handles. Box3D remains the source
 * of stable shape identity; raw shape handles are lifecycle metadata, while
 * raw body handles remain temporary lifecycle-scoped routing keys.
 */
export class PhysicsEntityRegistry {
  private activeWorld?: WorldIdentity;
  private readonly inner: ReturnType<typeof createEntityRegistry<PhysicsEntityAddress>>;

  constructor(worldId?: WorldIdentity) {
    if (worldId !== undefined) {
      assertWorldIdentity(worldId, 'worldId');
      this.activeWorld = worldId;
    }
    this.inner = createEntityRegistry({
      ...(worldId === undefined ? {} : { worldKey: worldId.key }),
      addressKey,
      addressWorldKey,
      groupKey,
    });
  }

  registerBody(body: BodyHandle, address: PhysicsEntityAddress): RegisteredBody {
    const validatedAddress = checkedAddress(address);
    this.assertWorld(validatedAddress.worldId);
    return this.inner.registerBody(body, validatedAddress);
  }

  registerShape(shape: ShapeHandle, body: BodyHandle, identity: ShapeIdentity): RegisteredShape {
    return this.inner.registerShape(shape, body, identity);
  }

  resolveBody(body: BodyHandle): RegisteredBody | undefined {
    return this.inner.resolveBody(body);
  }

  resolveShape(identity: ShapeIdentity): RegisteredShape | undefined {
    return this.inner.resolveShape(identity);
  }

  unregisterShape(identity: ShapeIdentity): boolean {
    return this.inner.unregisterShape(identity);
  }

  unregisterBody(body: BodyHandle): boolean {
    return this.inner.unregisterBody(body);
  }

  /** Remove only bodies (and their shapes) owned by the supplied structure. */
  unregisterStructure(structureId: StructureIdentity): number {
    assertIdentity(structureId, 'structure', 'structureId');
    assertAttached(structureId, this.worldForIdentity(structureId), 'structureId');
    return this.inner.unregisterGroup(structureId.key);
  }

  /** Alias using the noun used by the structure lifecycle API. */
  removeStructure(structureId: StructureIdentity): number {
    return this.unregisterStructure(structureId);
  }

  clear(): void {
    this.inner.clear();
  }

  getStats(): PhysicsEntityRegistryStats {
    const stats = this.inner.getStats();
    return Object.freeze({
      bodyCount: stats.bodyCount,
      shapeCount: stats.shapeCount,
      structureCount: stats.groupCount,
      structureBodyCount: stats.groupBodyCount,
      worldActorCount: stats.ungroupedBodyCount,
    });
  }

  snapshot(): PhysicsEntityRegistrySnapshot {
    const snapshot = this.inner.snapshot();
    return Object.freeze({
      stats: this.getStats(),
      bodies: snapshot.bodies,
      shapes: snapshot.shapes,
    });
  }

  private assertWorld(worldId: WorldIdentity): void {
    if (this.activeWorld === undefined) {
      this.activeWorld = worldId;
      return;
    }
    if (this.activeWorld.key !== worldId.key) {
      throw new Error('address belongs to a different physics world');
    }
  }

  private worldForIdentity(identity: StructureIdentity): WorldIdentity {
    const parent = identity.parent;
    if (parent === undefined || parent.kind !== 'world') {
      throw new Error('structureId must be attached to a world identity');
    }
    assertWorldIdentity(parent, 'structureId.parent');
    this.assertWorld(parent);
    return parent;
  }
}
